/**
 * 已保存路径（DESIGN.md §7.1 补充：Paths 面板，参照 Photoshop；ADR-0011）。
 * 全部落地走同一条 `createSetPathsCommand`（替换整份列表），跟参考线
 * （`app/guides.ts`）同一种"结构命令只管落地，语义留给调用方"的分工。
 *
 * 新 id 由调用方生成（`nextId` 参数），跟 `app/flatten.ts`/`app/rasterize.ts`
 * 同一个既有约定——id 生成集中在 UI 层（`main.tsx` 的 `nextGeneratedPathId`），
 * 这里不内置任何计数器/时间戳逻辑。
 */
import { createSetPathsCommand } from './commands/canvas-commands';
import { fillSelectionWithColor } from './selection-edit';
import { pathToSelection } from '../engine/path/rasterize';
import type { PathData, SavedPath } from '../document/types';
import type { AppStore } from './store';

/** 保存一条新路径（钢笔工具"路径"模式提交后的出口之一）。返回是否执行。 */
export function savePath(
  store: AppStore,
  path: PathData,
  nextId: () => string,
  name?: string,
): boolean {
  const document = store.document;
  if (document === null) return false;
  const saved: SavedPath = {
    id: nextId(),
    name: name ?? `路径 ${document.paths.length + 1}`,
    path,
  };
  store.dispatch(createSetPathsCommand([...document.paths, saved]));
  return true;
}

/** 重命名一条已保存路径。 */
export function renamePath(store: AppStore, id: string, name: string): boolean {
  const document = store.document;
  if (document === null) return false;
  if (!document.paths.some((p) => p.id === id)) return false;
  store.dispatch(
    createSetPathsCommand(document.paths.map((p) => (p.id === id ? { ...p, name } : p))),
  );
  return true;
}

/** 删除一条已保存路径。 */
export function deletePath(store: AppStore, id: string): boolean {
  const document = store.document;
  if (document === null) return false;
  if (!document.paths.some((p) => p.id === id)) return false;
  store.dispatch(createSetPathsCommand(document.paths.filter((p) => p.id !== id)));
  return true;
}

/** 复制一条已保存路径（新条目追加在列表末尾）。 */
export function duplicatePath(store: AppStore, id: string, nextId: () => string): boolean {
  const document = store.document;
  if (document === null) return false;
  const found = document.paths.find((p) => p.id === id);
  if (found === undefined) return false;
  const copy: SavedPath = { id: nextId(), name: `${found.name} 拷贝`, path: found.path };
  store.dispatch(createSetPathsCommand([...document.paths, copy]));
  return true;
}

/**
 * 已保存路径 → 选区（Paths 面板"建立选区"按钮）。跟 `pen-tool.ts` 的
 * `#convertAnchorsToSelectionAndHide` 同一套底层函数（`pathToSelection`），
 * 只是这里的选区不需要清空——它读的是持久化的 `SavedPath`，不是钢笔
 * 会话里那条临时工作路径，转换完路径本身还留在列表里。
 */
export function loadPathSelection(store: AppStore, id: string): boolean {
  const document = store.document;
  if (document === null) return false;
  const found = document.paths.find((p) => p.id === id);
  if (found === undefined) return false;
  const mask = pathToSelection(
    document.size.width,
    document.size.height,
    found.path.commands,
    found.path.fillRule,
  );
  store.setSelection(mask);
  return true;
}

/**
 * 已保存路径 → 选区 → 用前景色填充活动图层（Paths 面板"填充路径"按钮，
 * 真实 PS 也是默认填前景色，不弹选择填充源的对话框）。
 */
export function fillPath(store: AppStore, id: string): boolean {
  if (!loadPathSelection(store, id)) return false;
  return fillSelectionWithColor(store, store.foreground);
}
